/**
 * Widget Registry — defines all 30 available dashboard widgets.
 * Each widget has: id, title, description, category, defaultChartType,
 * supportedChartTypes, defaultEnabled, defaultSize (sm/md/lg)
 */

export const CHART_TYPES = [
  { value: 'bar',    label: 'Bar Chart' },
  { value: 'line',   label: 'Line Chart' },
  { value: 'area',   label: 'Area Chart' },
  { value: 'pie',    label: 'Pie Chart' },
  { value: 'donut',  label: 'Donut Chart' },
  { value: 'table',  label: 'Table' },
];

export const DEFAULT_DATE_RANGES = [
  { value: '7d',      label: 'Last 7 days' },
  { value: '30d',     label: 'Last 30 days' },
  { value: '3m',      label: 'Last 3 months' },
  { value: '6m',      label: 'Last 6 months' },
  { value: '1y',      label: 'Last 12 months' },
  { value: 'fy',      label: 'Current Financial Year' },
  { value: 'custom',  label: 'Custom range' },
];

export const WIDGET_REGISTRY = [
  // ── Profile & Quick Info ─────────────────────────────────────────────────
  {
    id: 'user-profile',
    title: 'User Profile',
    description: 'Your name, title, email and quick access to settings.',
    category: 'Profile',
    defaultChartType: 'table',
    supportedChartTypes: ['table'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: false,
  },
  {
    id: 'req-summary',
    title: 'Requisition Summary',
    description: 'Total requisitions, value, and status breakdown.',
    category: 'Requisitions',
    defaultChartType: 'table',
    supportedChartTypes: ['table', 'bar', 'pie', 'donut'],
    defaultEnabled: true,
    defaultSize: 'md',
    hasDateRange: false,
  },
  {
    id: 'req-by-status',
    title: 'Requisitions by Status',
    description: 'Visual breakdown of requisitions across all statuses.',
    category: 'Requisitions',
    defaultChartType: 'donut',
    supportedChartTypes: ['bar', 'pie', 'donut'],
    defaultEnabled: true,
    defaultSize: 'md',
    hasDateRange: false,
  },
  {
    id: 'req-recent',
    title: 'Recent Requisitions',
    description: 'The most recent requisitions created in the system.',
    category: 'Requisitions',
    defaultChartType: 'table',
    supportedChartTypes: ['table'],
    defaultEnabled: true,
    defaultSize: 'lg',
    hasDateRange: false,
  },
  {
    id: 'total-orders',
    title: 'Total Orders',
    description: 'Total number of purchase orders in the system.',
    category: 'Orders',
    defaultChartType: 'table',
    supportedChartTypes: ['table'],
    defaultEnabled: true,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  {
    id: 'orders-by-status',
    title: 'Orders by Status',
    description: 'Breakdown of purchase orders by current status.',
    category: 'Orders',
    defaultChartType: 'table',
    supportedChartTypes: ['table', 'bar', 'pie', 'donut'],
    defaultEnabled: true,
    defaultSize: 'md',
    hasDateRange: false,
  },
  {
    id: 'invoices-pending',
    title: 'Invoices Pending',
    description: 'Unprocessed invoices waiting for approval or action.',
    category: 'Finance',
    defaultChartType: 'table',
    supportedChartTypes: ['table'],
    defaultEnabled: true,
    defaultSize: 'md',
    hasDateRange: false,
  },
  {
    id: 'spend-analysis-links',
    title: 'Spend Analysis Reports',
    description: 'Quick links to all spend analysis reports.',
    category: 'Reports',
    defaultChartType: 'table',
    supportedChartTypes: ['table'],
    defaultEnabled: true,
    defaultSize: 'sm',
    hasDateRange: false,
  },
  // ── Approvals & Pending ──────────────────────────────────────────────────
  {
    id: 'to-be-approved',
    title: 'To Be Approved by Me',
    description: 'Pending approvals requiring your action.',
    category: 'Approvals',
    defaultChartType: 'table',
    supportedChartTypes: ['table'],
    defaultEnabled: true,
    defaultSize: 'md',
    hasDateRange: false,
  },
  {
    id: 'my-submissions',
    title: 'My Submissions Yet to Be Approved',
    description: 'Your submitted orders/requests awaiting approval.',
    category: 'Approvals',
    defaultChartType: 'table',
    supportedChartTypes: ['table'],
    defaultEnabled: true,
    defaultSize: 'md',
    hasDateRange: false,
  },
  {
    id: 'all-overdue-approvals',
    title: 'All Overdue Approvals',
    description: 'Approvals past their due date.',
    category: 'Approvals',
    defaultChartType: 'donut',
    supportedChartTypes: ['donut', 'pie', 'bar', 'table'],
    defaultEnabled: true,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  {
    id: 'overdue-items',
    title: 'Overdue Items',
    description: 'Items that are overdue for delivery or action.',
    category: 'Orders',
    defaultChartType: 'bar',
    supportedChartTypes: ['bar', 'line', 'table'],
    defaultEnabled: true,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  // ── Orders & Spend ───────────────────────────────────────────────────────
  {
    id: 'my-orders',
    title: 'My Orders',
    description: 'Order value trend over time for your orders.',
    category: 'Orders',
    defaultChartType: 'line',
    supportedChartTypes: ['line', 'bar', 'area', 'pie', 'donut'],
    defaultEnabled: true,
    defaultSize: 'md',
    hasDateRange: true,
  },
  {
    id: 'top-ordered-items',
    title: 'Top Ordered Items',
    description: 'Most frequently ordered items by quantity.',
    category: 'Orders',
    defaultChartType: 'bar',
    supportedChartTypes: ['bar', 'line', 'pie', 'donut'],
    defaultEnabled: true,
    defaultSize: 'md',
    hasDateRange: true,
  },
  {
    id: 'top-expenses',
    title: 'Top Expenses',
    description: 'Highest expense categories.',
    category: 'Finance',
    defaultChartType: 'bar',
    supportedChartTypes: ['bar', 'line', 'pie', 'donut'],
    defaultEnabled: true,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  {
    id: 'recent-history',
    title: 'Recent History',
    description: 'Recent requests, orders, and RFQs.',
    category: 'Orders',
    defaultChartType: 'table',
    supportedChartTypes: ['table'],
    defaultEnabled: true,
    defaultSize: 'sm',
    hasDateRange: false,
  },
  // ── Contracts & Suppliers ────────────────────────────────────────────────
  {
    id: 'top-contract-values',
    title: 'Top Contract Values',
    description: 'Highest value supplier contracts.',
    category: 'Contracts',
    defaultChartType: 'pie',
    supportedChartTypes: ['pie', 'donut', 'bar', 'table'],
    defaultEnabled: true,
    defaultSize: 'sm',
    hasDateRange: false,
  },
  {
    id: 'top-discounts-supplier',
    title: 'Top Discounts by Supplier',
    description: 'Negotiated discounts by supplier.',
    category: 'Suppliers',
    defaultChartType: 'bar',
    supportedChartTypes: ['bar', 'pie', 'donut'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  {
    id: 'top-spend-by-supplier',
    title: 'Top Spend by Supplier Diversity',
    description: 'Spend analysis by supplier diversity classification.',
    category: 'Suppliers',
    defaultChartType: 'bar',
    supportedChartTypes: ['bar', 'pie', 'donut'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  // ── Documents & Currencies ───────────────────────────────────────────────
  {
    id: 'orders-by-document-type',
    title: 'Orders by Document Type',
    description: 'Order distribution across document types.',
    category: 'Orders',
    defaultChartType: 'pie',
    supportedChartTypes: ['pie', 'donut', 'bar'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  {
    id: 'top-delivery-invoice-points',
    title: 'Top Delivery / Invoice Points',
    description: 'Most used delivery and invoice locations.',
    category: 'Orders',
    defaultChartType: 'pie',
    supportedChartTypes: ['pie', 'donut', 'bar'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  {
    id: 'order-by-currency',
    title: 'Order by Currency',
    description: 'Order values broken down by currency.',
    category: 'Finance',
    defaultChartType: 'donut',
    supportedChartTypes: ['donut', 'pie', 'bar'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  // ── Budget & Finance ─────────────────────────────────────────────────────
  {
    id: 'account-codes-budget',
    title: 'Account Codes Budget',
    description: 'Budget vs spend across account code segments.',
    category: 'Finance',
    defaultChartType: 'bar',
    supportedChartTypes: ['bar', 'line', 'area'],
    defaultEnabled: false,
    defaultSize: 'md',
    hasDateRange: true,
  },
  {
    id: 'external-invoice',
    title: 'External Invoice',
    description: 'Invoice status breakdown (Success / Invoice / Fail).',
    category: 'Finance',
    defaultChartType: 'donut',
    supportedChartTypes: ['donut', 'pie', 'bar'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  // ── Carbon & Sustainability ──────────────────────────────────────────────
  {
    id: 'item-carbon-emissions',
    title: 'Item Carbon Emissions',
    description: 'CO\u2082e emissions by item.',
    category: 'Sustainability',
    defaultChartType: 'bar',
    supportedChartTypes: ['bar', 'pie', 'donut'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  {
    id: 'supplier-carbon-emissions',
    title: 'Supplier Carbon Emissions',
    description: 'CO\u2082e emissions by supplier.',
    category: 'Sustainability',
    defaultChartType: 'donut',
    supportedChartTypes: ['donut', 'pie', 'bar'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  {
    id: 'order-carbon-emissions',
    title: 'Order Carbon Emissions',
    description: 'CO\u2082e emissions by purchase order.',
    category: 'Sustainability',
    defaultChartType: 'pie',
    supportedChartTypes: ['pie', 'donut', 'bar'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  {
    id: 'category-carbon-emissions',
    title: 'Category Carbon Emissions',
    description: 'CO\u2082e emissions by spend category.',
    category: 'Sustainability',
    defaultChartType: 'bar',
    supportedChartTypes: ['bar', 'pie', 'donut'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  {
    id: 'monthly-po-emissions',
    title: 'Monthly PO Emissions',
    description: 'Monthly CO\u2082e trend from purchase orders.',
    category: 'Sustainability',
    defaultChartType: 'bar',
    supportedChartTypes: ['bar', 'line', 'area'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  // ── Throughput / Cycle Times ──────────────────────────────────────────────
  {
    id: 'throughput-metrics',
    title: 'Procurement Cycle Times',
    description: 'Average cycle times across the procurement lifecycle — requisitions, orders, goods receipt, invoicing, and payment.',
    category: 'Performance',
    defaultChartType: 'table',
    supportedChartTypes: ['table', 'bar'],
    defaultEnabled: true,
    defaultSize: 'md',
    hasDateRange: false,
  },
  // ── Inventory ────────────────────────────────────────────────────────────
  {
    id: 'reorder-level-stock',
    title: 'Re-Order Level Stock',
    description: 'Items approaching reorder levels.',
    category: 'Inventory',
    defaultChartType: 'bar',
    supportedChartTypes: ['bar', 'line', 'table'],
    defaultEnabled: false,
    defaultSize: 'md',
    hasDateRange: false,
  },
  // ── RFQ / RFP ────────────────────────────────────────────────────────────
  {
    id: 'rfp-analysis',
    title: 'My RFP Analysis',
    description: 'RFP open opportunities vs submissions.',
    category: 'Sourcing',
    defaultChartType: 'pie',
    supportedChartTypes: ['pie', 'donut', 'bar'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  {
    id: 'rfq-analysis',
    title: 'My RFQ Analysis',
    description: 'RFQ status breakdown: Issued, Awarded, Logged, Submissions.',
    category: 'Sourcing',
    defaultChartType: 'pie',
    supportedChartTypes: ['pie', 'donut', 'bar'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  {
    id: 'supplier-rfq-analysis',
    title: 'Supplier RFQ Analysis',
    description: 'RFQ issued vs replied by supplier.',
    category: 'Sourcing',
    defaultChartType: 'bar',
    supportedChartTypes: ['bar', 'pie', 'donut'],
    defaultEnabled: false,
    defaultSize: 'md',
    hasDateRange: true,
  },
  {
    id: 'rfq-pending-responses',
    title: 'RFQ Pending Supplier Responses',
    description: 'Days since last supplier response per RFQ.',
    category: 'Sourcing',
    defaultChartType: 'line',
    supportedChartTypes: ['line', 'bar'],
    defaultEnabled: false,
    defaultSize: 'md',
    hasDateRange: false,
  },
  // ── Supplier Performance ─────────────────────────────────────────────────
  {
    id: 'supplier-performance-lead-time',
    title: 'Supplier Performance — Lead Time',
    description: 'Delivery lead time distribution by supplier.',
    category: 'Suppliers',
    defaultChartType: 'donut',
    supportedChartTypes: ['donut', 'pie', 'bar'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  {
    id: 'low-emission-category',
    title: 'Low Emission Category',
    description: 'Categories with lowest carbon emissions.',
    category: 'Sustainability',
    defaultChartType: 'bar',
    supportedChartTypes: ['bar', 'pie', 'donut'],
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: true,
  },
  {
    id: 'pending-supplier-approvals',
    title: 'Suppliers Pending Approval',
    description: 'Supplier registrations awaiting vendor manager review',
    category: 'Suppliers',
    supportedChartTypes: ['table'],
    defaultChartType: 'table',
    defaultEnabled: false,
    defaultSize: 'sm',
    hasDateRange: false,
    vendorManagerOnly: true,
  },
];

export const WIDGET_CATEGORIES = [...new Set(WIDGET_REGISTRY.map(w => w.category))];

export function getDefaultPreferences() {
  return WIDGET_REGISTRY.reduce((acc, widget, index) => {
    acc[widget.id] = {
      enabled: widget.defaultEnabled,
      position: index,
      chartType: widget.defaultChartType,
      dateRange: '6m',
      dateFrom: '',
      dateTo: '',
    };
    return acc;
  }, {});
}
